import React, { useEffect, useState } from 'react';
import { Empty } from 'antd';
import Loading from '@components/Loading';
import Chart from '@components/charts/Chart';
import { COLORS } from '@components/charts/chart.constant';
import { apiGetConsumptionComparisonByBranch } from '@services/ManageDevices/DeviceServices';
import { showError } from '@components/notification';
import { formatCurrency } from '@utils/utils';

const ConsumptionComparison = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [categories, setCategories] = useState([]);

  // Convert branch name to abbreviated form
  const convertToAbbreviation = (branchName) => {
    if (!branchName) return '';
    const cleanName = branchName.replace(/Nhà máy\s*/gi, '').trim();
    // return cleanName
    //   .split(' ')
    //   .map((word) => word.charAt(0).toUpperCase())
    //   .join('');
    return branchName;
  };

  // Format period to "Kỳ M/YYYY" format
  const formatPeriodLabel = (periodStr) => {
    if (!periodStr) return '';
    const date = new Date(periodStr);
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `Kỳ ${month}/${year}`;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await apiGetConsumptionComparisonByBranch();
      if (res.data?.data?.branches && Array.isArray(res.data.data.branches)) {
        const branchesData = res.data.data.branches;
        const periods = res.data.data.periods || [];

        // Extract branch names as categories (abbreviated)
        const branches = branchesData.map(
          (branch) => convertToAbbreviation(branch.branch_name) || branch.branch_code
        );
        setCategories(branches);

        // Chỉ lấy 3 kỳ gần nhất và đảo ngược để hiển thị từ cũ đến mới
        const recentPeriods = periods.slice(-3).reverse();

        // Màu sắc cho từng kỳ: từ cũ đến mới
        const colors = ['#9E9E9E', '#FF9800', '#4f46e5d9']; // Xám, Cam, Blue đậm

        // Build series data for each period (từ cũ đến mới)
        const series = recentPeriods.map((period, periodIndex) => {
          // Tìm chỉ số trong mảng gốc
          const originalIndex = periods.findIndex((p) => p === period);

          return {
            name: formatPeriodLabel(period),
            data: branchesData.map((branch) => {
              const consumption = branch.consumptions?.[originalIndex]?.consumption || 0;
              return parseFloat(consumption);
            }),
            color: colors[periodIndex] // Gán màu tương ứng
          };
        });

        setData(series);
      }
    } catch (error) {
      console.error('Error fetching consumption comparison:', error);
      showError('Không thể tải dữ liệu so sánh');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Auto refresh every 5 minutes
    const interval = setInterval(fetchData, 300000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <Loading />
      </div>
    );
  }

  if (data.length === 0) {
    return <Empty description="Chưa có dữ liệu" />;
  }

  return (
    <div className="chart-container">
      <Chart
        options={{
          colors: data.map((s) => s.color), // Sử dụng màu từ series
          chart: {
            zoom: {
              enabled: false
            },
            toolbar: {
              show: true
            }
          },
          plotOptions: {
            bar: {
              horizontal: false,
              columnWidth: '70%',
              // borderRadius: 0,
              dataLabels: {
                position: 'top'
              }
            }
          },
          dataLabels: {
            enabled: false
          },
          stroke: {
            show: true,
            width: 2,
            colors: ['transparent']
          },
          xaxis: {
            categories: categories,
            labels: {
              style: {
                fontSize: '12px'
              }
            }
          },
          yaxis: {
            title: {
              text: 'Tiêu thụ (m³)'
            },
            labels: {
              formatter: (val) => formatCurrency(val, 0)
            }
          },
          grid: {
            show: true
          },
          tooltip: {
            y: {
              formatter: (val) => `${formatCurrency(val, 2)} m³`
            }
          },
          legend: {
            position: 'top',
            horizontalAlign: 'left',
            offsetY: 0
          }
        }}
        type="bar"
        series={data}
        height={350}
      />
    </div>
  );
};

export default ConsumptionComparison;
